import {createSlice, createAsyncThunk} from '@reduxjs/toolkit'
import {AuthCheck, DownloadFileFn} from '../../helpers'
import {
  CreateSubcontractorDocumentClient,
  SearchSubcontractorDocumentClient,
  GetSubcontractorDocumentClient,
  UpdateSubcontractorDocumentClient,
  DeleteSubcontractorDocumentClient,
  GetSubcontractorDocumentMailContentClient,
  SendSubcontractorDocumentMailClient,
  GetSubcontractorDocumentByGuidClient,
  UploadSubcontractorDocumentFileClient,
  CreateSubcontractorDocumentFileClient,
} from '../../api'

const SERVICE_URL = window.ENVVARS.REACT_APP_SERVICE_URL

// API Clients
const createSubcontractorDocumentClient = new CreateSubcontractorDocumentClient(SERVICE_URL)
const searchSubcontractorDocumentClient = new SearchSubcontractorDocumentClient(SERVICE_URL)
const getSubcontractorDocumentClient = new GetSubcontractorDocumentClient(SERVICE_URL)
const updateSubcontractorDocumentClient = new UpdateSubcontractorDocumentClient(SERVICE_URL)
const deleteSubcontractorDocumentClient = new DeleteSubcontractorDocumentClient(SERVICE_URL)
const getSubcontractorDocumentMailContentClient = new GetSubcontractorDocumentMailContentClient(
  SERVICE_URL
)
const sendSubcontractorDocumentMailClient = new SendSubcontractorDocumentMailClient(SERVICE_URL)
const getSubcontractorDocumentByGuidClient = new GetSubcontractorDocumentByGuidClient(SERVICE_URL)
const uploadSubcontractorDocumentFileClient = new UploadSubcontractorDocumentFileClient(SERVICE_URL)
const createSubcontractorDocumentFileClient = new CreateSubcontractorDocumentFileClient(SERVICE_URL)

const initialState = {
  loading: false,
  isRedirect: false,
  error: null,
  isDeleted: false,
  excel: null,
  list: null,
  detail: null,
  mailContent: null,
  isMailSent: false,
  publicDetail: null,
  isUploaded: false,
  isFileAdded: false,
}

// Async Thunks
export const subcontractorDocumentCreate = createAsyncThunk(
  'subcontractorDocument/create',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await createSubcontractorDocumentClient.create(params)
      dispatch(setRedirect(response || true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const subcontractorDocumentSearch = createAsyncThunk(
  'subcontractorDocument/search',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchSubcontractorDocumentClient.search(
        params.name,
        params.companyId,
        params.projectId,
        params.year,
        params.month,
        params.subcontractorDocumentStateId,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      dispatch(setList(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const subcontractorDocumentExcel = createAsyncThunk(
  'subcontractorDocument/excel',
  async ({params, fileName}, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchSubcontractorDocumentClient.excel(
        params.name,
        params.companyId,
        params.projectId,
        params.year,
        params.month,
        params.subcontractorDocumentStateId,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      if (response && response.data) {
        DownloadFileFn(response.data, fileName)
      }
      dispatch(setExcel(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const subcontractorDocumentGet = createAsyncThunk(
  'subcontractorDocument/get',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await getSubcontractorDocumentClient.get(params)
      dispatch(setDetail(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const subcontractorDocumentUpdate = createAsyncThunk(
  'subcontractorDocument/update',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await updateSubcontractorDocumentClient.update(params)
      dispatch(setRedirect(response || params || true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const subcontractorDocumentDelete = createAsyncThunk(
  'subcontractorDocument/delete',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setDeleted(false))
      const response = await deleteSubcontractorDocumentClient.delete(params)
      dispatch(setDeleted(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const subcontractorDocumentMailContent = createAsyncThunk(
  'subcontractorDocument/mailContent',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await getSubcontractorDocumentMailContentClient.get(params)
      dispatch(setMailContent(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const subcontractorDocumentSendMail = createAsyncThunk(
  'subcontractorDocument/sendMail',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setMailSent(false))
      const response = await sendSubcontractorDocumentMailClient.send(params)
      dispatch(setMailSent(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const subcontractorDocumentGetByGuid = createAsyncThunk(
  'subcontractorDocument/getByGuid',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await getSubcontractorDocumentByGuidClient.get(params)
      dispatch(setPublicDetail(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const subcontractorDocumentUploadFile = createAsyncThunk(
  'subcontractorDocument/uploadFile',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setUploaded(false))
      const response = await uploadSubcontractorDocumentFileClient.upload(
        params.SubcontractorDocumentGuid,
        params.FormFile,
        params.Description
      )
      dispatch(setUploaded(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const subcontractorDocumentCreateFile = createAsyncThunk(
  'subcontractorDocument/createFile',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setFileAdded(false))
      const response = await createSubcontractorDocumentFileClient.create(
        params.Id,
        params.FormFile,
        params.Description
      )
      dispatch(setFileAdded(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

// Slice
const subcontractorDocumentSlice = createSlice({
  name: 'subcontractorDocument',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
      state.loading = false
    },
    setRedirect: (state, action) => {
      state.isRedirect = action.payload
      state.loading = false
    },
    setList: (state, action) => {
      state.list = action.payload
      state.loading = false
    },
    setDetail: (state, action) => {
      state.detail = action.payload
      state.loading = false
    },
    setDeleted: (state, action) => {
      state.isDeleted = action.payload
      state.loading = false
    },
    setExcel: (state, action) => {
      state.excel = action.payload
      state.loading = false
    },
    setMailContent: (state, action) => {
      state.mailContent = action.payload
      state.loading = false
    },
    setMailSent: (state, action) => {
      state.isMailSent = action.payload
      state.loading = false
    },
    setPublicDetail: (state, action) => {
      state.publicDetail = action.payload
      state.loading = false
    },
    setUploaded: (state, action) => {
      state.isUploaded = action.payload
      state.loading = false
    },
    setFileAdded: (state, action) => {
      state.isFileAdded = action.payload
      state.loading = false
    },
  },
})

export const {
  setLoading,
  setError,
  setRedirect,
  setList,
  setDetail,
  setDeleted,
  setExcel,
  setMailContent,
  setMailSent,
  setPublicDetail,
  setUploaded,
  setFileAdded,
} = subcontractorDocumentSlice.actions

export default subcontractorDocumentSlice.reducer
